import React from "react";
import { Logo } from "../brand/Logo";
import { Button } from "../core/Button";
import { IconButton } from "../core/IconButton";
import { LanguageToggle } from "./LanguageToggle";
import { useViewport } from "../core/Viewport";

/**
 * PORT NOTE (DY1.4): `renderLink` is new. The design system ships without a
 * router, so nav items are bare `<a href>` — inside the app every one of those
 * is a full page reload, which throws away the SPA and re-downloads the bundle
 * on every nav click. `renderLink({ href, label, active, style, onClose })` lets
 * the app hand back a react-router `Link`; omitted, the anchor behaviour is
 * unchanged.
 *
 * PORT NOTE (DY1.4, revised T12-M1): the mobile bar, and `compact`.
 *
 * The drawer this component used to open below 768px is gone. Below **992px**
 * the app navigates with a bottom tab bar, and this header shrinks to the mark
 * alone: the marketing links, the language toggle and the auth buttons are all
 * rows in that bar's Menú sheet, and a header that also offered them would be
 * the second navigation system the bar exists to remove.
 *
 * `compact` is the one caller that keeps the old shape, and it is the Dany
 * thread. The tab bar hides on a full-screen conversation, so that header is
 * the only way out and the only place a signed-out reader reaches "sign in" —
 * which is why `compact` passes no links: that is what frees the width for the
 * two auth buttons at 360px, in either language.
 *
 * The burger still exists for a `compact: false` caller with links ABOVE 992px
 * — there is none today, and the branch is left rather than deleted so the
 * component keeps working standalone in the design system.
 *
 * PORT NOTE (T14-M1): `shellRight`, the one slot the shell header has.
 *
 * The mark alone left the right end of this header empty below 992px, and the
 * notification bell — which the app had above that width and nowhere below it
 * — had no place to live. `right` cannot be that place: it holds the auth
 * buttons, which the Menú sheet already carries and which are exactly what this
 * header drops. So a second slot, rendered ONLY in the shell bar, for the one
 * control a signed-in reader needs in the header itself. Passing nothing is the
 * anonymous case and leaves the bar as it was.
 *
 * `compact` does not get it: that bar is the Dany thread's only way out and
 * already spends its 360px on the two auth buttons.
 *
 * This is a deliberate deviation from SPEC_NAVEGACION_MOVIL, whose header is
 * the mark and nothing else. Authorised by Jorge for T14.
 */
export function Navbar({
  links = [], activeHref, logoSrc, lang = "en", onLangChange,
  signedIn = false, right, shellRight, onLogin, onSignup, renderLink,
  loginLabel = "Log in", signupLabel = "Sign up", menuLabel = "Menu",
  compact = false,
}) {
  const { isNavMobile } = useViewport();
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => { if (!isNavMobile) setOpen(false); }, [isNavMobile]);

  // T12-M1. Below 992px the bottom tab bar is the navigation, so this header
  // keeps only the mark — no links, no burger, no toggle. The four marketing
  // links and the language toggle are rows in the bar's Menú sheet, and the
  // auth buttons are its last two.
  //
  // `compact` is the exception, and it is the Dany thread: the tab bar hides on
  // that screen, so its header is the only way out and the only place a signed
  // -out reader can reach "sign in". It keeps the bar it already had.
  const shellNav = isNavMobile && !compact;

  const linkStyle = (on, big) => ({
    padding: big ? "12px 14px" : "8px 12px",
    minHeight: big ? "var(--touch-min)" : undefined,
    display: big ? "flex" : undefined,
    alignItems: big ? "center" : undefined,
    borderRadius: "var(--radius-sm)",
    fontFamily: "var(--font-display)",
    fontSize: big ? "var(--text-base)" : "var(--text-sm)",
    fontWeight: "var(--weight-medium)",
    textDecoration: "none",
    background: on ? "var(--surface-muted)" : "transparent",
    color: on ? "var(--text-body)" : "var(--text-muted)",
    transition: "background-color var(--duration-base) var(--ease-out), color var(--duration-base) var(--ease-out)",
  });

  const auth = right ? right : signedIn ? null : (
    <>
      <Button variant="ghost" size="sm" onClick={onLogin}>{loginLabel}</Button>
      <Button variant="secondary" size="sm" onClick={onSignup}>{signupLabel}</Button>
    </>
  );

  return (
    <header style={{
      position: "sticky", top: 0, zIndex: 40, width: "100%",
      background: "var(--glass-bg)", backdropFilter: "var(--glass-blur)",
      WebkitBackdropFilter: "var(--glass-blur)",
      borderBottom: "1px solid var(--border-default)",
    }}>
      <div style={{
        maxWidth: "var(--container-max)", margin: "0 auto",
        minHeight: "var(--navbar-height)", padding: "0 var(--section-padding-x)",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-4)",
      }}>
        {/* The wordmark comes back below 992px: with the burger and the toggle
            gone, the ~105px it costs is width nothing else is asking for. It
            still goes in the thread's compact bar, which spends that width on
            the auth buttons instead. */}
        <Logo size={isNavMobile ? 30 : 36} src={logoSrc}
          withWordmark={!isNavMobile || shellNav} />

        {!isNavMobile && (
          <nav style={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            {links.map((l, i) => {
              const active = l.href === activeHref;
              const style = linkStyle(active);
              return renderLink
                ? renderLink({ ...l, active, style, key: `${l.href}-${l.label}-${i}` })
                : (
                  <a key={`${l.href}-${l.label}-${i}`} href={l.href} onClick={l.onClick} style={style}>
                    {l.label}
                  </a>
                );
            })}
          </nav>
        )}

        {/* The shell bar's right end (T14-M1): the mark on one side, whatever the
            app puts here on the other. Empty for an anonymous visitor, which is
            the bar exactly as T12-M1 left it. */}
        {shellNav && shellRight && (
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
            {shellRight}
          </div>
        )}

        {/* Mobile bar, by arithmetic: mark 30 + toggle 112 + burger 44 + gaps
            fits 360px with room; adding the two auth buttons to that does not,
            in either language. So the burger keeps the auth buttons wherever
            there are links to hold them, and the thread — which has no links —
            spends that width on the auth buttons instead. */}
        {!shellNav && (
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
            <LanguageToggle value={lang} onChange={onLangChange} />
            {isNavMobile && links.length > 0
              ? <IconButton icon={open ? "x" : "menu"} label={menuLabel} onClick={() => setOpen((v) => !v)} />
              : auth}
          </div>
        )}
      </div>

      {!shellNav && isNavMobile && links.length > 0 && open && (
        <div style={{
          borderTop: "1px solid var(--border-default)", background: "var(--canvas)",
          padding: "var(--space-4) var(--section-padding-x) var(--space-5)",
          display: "grid", gap: "var(--space-2)",
        }}>
          {links.map((l, i) => {
            const active = l.href === activeHref;
            const style = linkStyle(active, true);
            const close = () => setOpen(false);
            return renderLink
              ? renderLink({ ...l, active, style, key: `m-${l.href}-${i}`, onClose: close })
              : (
                <a key={`m-${l.href}-${i}`} href={l.href}
                  onClick={(e) => { close(); l.onClick?.(e); }}
                  style={style}>{l.label}</a>
              );
          })}
          <div style={{ display: "grid", gap: "var(--space-2)" }}>{auth}</div>
        </div>
      )}
    </header>
  );
}
